import { useCallback, useEffect, useState } from 'react';
import { UmsatzWert } from '@/components/UmsatzSperre';
import { toast } from 'sonner';
import { Copy, Ticket } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

/* Gastpässe (11.10.2026): Drop-Ins und P1 Kickstart (12 Einheiten) für Strength & Conditioning / HIIT.
   Gekauft wird online (Stripe), gebucht in der App. Hier: Übersicht, begründete Gutschrift einer
   Einheit und — nur Leitung — Erstattung über Stripe. */
const euro = (v) => `${Number(v || 0).toFixed(2).replace('.', ',')} €`;
const datum = (iso) => (iso ? String(iso).slice(0, 10).split('-').reverse().join('.') : '–');
const STATUS = { offen: ['Zahlung offen', 'text-amber-600'], bezahlt: ['Bezahlt', 'text-emerald-600'], erstattet: ['Erstattet', 'text-muted-foreground'], erstattung_laeuft: ['Erstattung läuft', 'text-amber-600'] };

export default function GastpaessePage() {
  const { user } = useAuth();
  const leitung = user?.role === 'admin' || user?.role === 'manager';
  const [d, setD] = useState(null);
  const [aktion, setAktion] = useState(null);      // { id, art: 'gutschrift' | 'erstatten' }
  const [grund, setGrund] = useState('');
  const [busy, setBusy] = useState(false);
  const laden = useCallback(() => api.get('/gastpass').then((r) => setD(r.data)).catch(() => toast.error('Gastpässe nicht geladen')), []);
  useEffect(() => { laden(); }, [laden]);

  const ausfuehren = async () => {
    if (grund.trim().length < 5) { toast.error('Bitte eine Begründung eintragen'); return; }
    setBusy(true);
    try {
      await api.post(`/gastpass/${aktion.id}/${aktion.art}`, { grund: grund.trim() });
      toast.success(aktion.art === 'gutschrift' ? 'Einheit gutgeschrieben' : 'Betrag über Stripe erstattet');
      setAktion(null); setGrund(''); laden();
    } catch (e) { const t = e?.response?.data?.detail; toast.error(typeof t === 'string' ? t : 'Das hat nicht geklappt'); }
    setBusy(false);
  };
  const kaufLink = `${window.location.origin}/gastpass`;

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-5xl">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-extrabold flex items-center gap-2"><Ticket className="w-6 h-6" /> Gastpässe</h1>
          <p className="text-sm text-muted-foreground">Strength &amp; Conditioning (Sa) und HIIT (So) für Nichtmitglieder: Drop-In 29 €, P1 Kickstart 180 € mit 12 Einheiten (nur Neukunden, einmalig). Höchstens vier Gastplätze je Kurs.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => { navigator.clipboard?.writeText(kaufLink).then(() => toast.success('Link kopiert')).catch(() => toast.message(kaufLink)); }} data-testid="gastpass-link-kopieren">
          <Copy className="w-4 h-4 mr-1" /> Kauf-Link kopieren
        </Button>
      </div>
      {d && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[[d.summen.bezahlt, 'Bezahlte Pässe'], [<UmsatzWert wert={d.summen.umsatz} format={euro} />, 'Umsatz (ohne Erstattete)'], [d.summen.einheiten_frei, 'Offene Einheiten'], [`${d.summen.kickstart_laufend} / ${d.summen.kickstart_plaetze}`, 'Kickstart laufend']].map(([w, l]) => (
            <Card key={l}><CardContent className="p-4"><div className="text-xs text-muted-foreground">{l}</div><div className="text-2xl font-extrabold tabular-nums">{w}</div></CardContent></Card>
          ))}
        </div>
      )}
      <Card><CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm" data-testid="gastpass-tabelle">
          <thead><tr className="text-left text-xs text-muted-foreground border-b">
            <th className="p-3">Gast</th><th className="p-3">Pass</th><th className="p-3">Einheiten</th><th className="p-3">Gültig bis</th><th className="p-3">Stand</th><th className="p-3" />
          </tr></thead>
          <tbody>
            {(d?.paesse || []).map((p) => (
              <tr key={p.id} className="border-b last:border-0 align-top">
                <td className="p-3 font-medium">{p.lead_name || '–'}<div className="text-[11px] text-muted-foreground">gekauft {datum(p.erstellt_am)}</div></td>
                <td className="p-3">{p.produkt === 'paket' ? 'P1 Kickstart' : 'Drop-In'}<div className="text-[11px] text-muted-foreground">{euro(p.preis)}</div></td>
                <td className="p-3 tabular-nums">{p.genutzt} von {p.einheiten} genutzt
                  {(p.spaetabsagen || []).length > 0 && <div className="text-[11px] text-amber-600">{p.spaetabsagen.length} Spätabsage{p.spaetabsagen.length > 1 ? 'n' : ''}</div>}
                  {(p.gutschriften || []).map((g, i) => <div key={i} className="text-[11px] text-muted-foreground">Gutschrift {datum(g.am)}: {g.grund}</div>)}
                </td>
                <td className="p-3">{datum(p.gueltig_bis)}</td>
                <td className={`p-3 ${STATUS[p.status]?.[1] || ''}`}>{STATUS[p.status]?.[0] || p.status}{p.erstattet && <div className="text-[11px]">{euro(p.erstattet.betrag)} · {p.erstattet.grund}</div>}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  {p.status === 'bezahlt' && p.genutzt > 0 && <Button size="sm" variant="outline" className="mr-1" onClick={() => { setAktion({ id: p.id, art: 'gutschrift' }); setGrund(''); }} data-testid={`gutschrift-${p.id}`}>Einheit gutschreiben</Button>}
                  {p.status === 'bezahlt' && leitung && <Button size="sm" variant="outline" className="text-red-600" onClick={() => { setAktion({ id: p.id, art: 'erstatten' }); setGrund(''); }} data-testid={`erstatten-${p.id}`}>Erstatten</Button>}
                  {aktion?.id === p.id && (
                    <div className="mt-2 flex gap-2 justify-end">
                      <Input value={grund} onChange={(e) => setGrund(e.target.value)} placeholder={aktion.art === 'gutschrift' ? 'Begründung, z. B. Attest' : 'Begründung der Erstattung'} className="h-8 w-56" data-testid="gastpass-grund" />
                      <Button size="sm" onClick={ausfuehren} disabled={busy}>{aktion.art === 'gutschrift' ? 'Gutschreiben' : `${euro(p.preis)} erstatten`}</Button>
                      <Button size="sm" variant="ghost" onClick={() => setAktion(null)}>Abbrechen</Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {d && d.paesse.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">Noch kein Gastpass verkauft.</td></tr>}
          </tbody>
        </table>
      </CardContent></Card>
    </div>
  );
}
